import { MONTH_LONG, MONTH_SHORT, WEEKDAY_LONG, WEEKDAY_SHORT, formatLongDate, parseDate } from './dates';
import { type Lookup, quantityOf } from './schedule';
import { shortName } from './palette';
import type { MinistryIconKey, ScheduleEvent } from './types';

/**
 * Conteúdo de "Compartilhar escala": o mesmo documento alimenta o texto (WhatsApp,
 * copiar), a imagem e o PDF. O recorte segue os filtros de quem compartilha — o
 * ministério escolhido e, na tela de escalas, o membro filtrado.
 */

export interface SharePerson {
  id: string;
  name: string;
  /** "Ana S." */
  short: string;
  /** Foto do avatar (`Member.photo`); sem ela, as iniciais. */
  photo: string | null;
  /** É o membro do recorte (destacado na imagem e no PDF). */
  highlight: boolean;
}

export interface ShareRow {
  role: string;
  people: SharePerson[];
  /** Vagas em aberto (0 quando completa). */
  vacancies: number;
}

export interface ShareSection {
  ministryId: string;
  ministry: string;
  color: string;
  /** O ícone do ministério, desenhado na pílula do nome. */
  icon: MinistryIconKey;
  rows: ShareRow[];
}

export interface ShareEvent {
  id: string;
  title: string;
  /** `yyyy-mm-dd` */
  date: string;
  time: string;
  /** "Domingo, 11 de outubro · 10:00" */
  when: string;
  sections: ShareSection[];
}

export interface ShareScope {
  /** Só as funções deste ministério. */
  ministryId?: string | null;
  /** Só as funções em que esta pessoa serve. */
  memberId?: string | null;
  /** Rótulo do período ("outubro de 2026"); sem ele, sai das datas dos eventos. */
  period?: string | null;
  /**
   * O mês ou a semana da tela do Calendário: com ele, o "Exportar" oferece também o modo
   * Calendário (`calendarDocument` de `shareCalendar`). Não muda a escala.
   */
  range?: { from: string; to: string; kind: 'month' | 'week' } | null;
}

export interface ShareDocument {
  /** "Escala de outubro de 2026"; de um evento só, "Escala". */
  title: string;
  /** "13 eventos · 3 vagas em aberto"; de um evento só, a data ("Domingo, 11 de outubro de 2026"). */
  subtitle: string;
  /** "outubro de 2026", "11 a 17 de outubro de 2026". */
  period: string;
  /** Ministério do recorte. */
  ministry: { name: string; color: string; icon: MinistryIconKey } | null;
  /** Membro do recorte. */
  member: string | null;
  events: ShareEvent[];
  /** Vagas em aberto no recorte todo. */
  vacancies: number;
}

/** "+1 vaga", "+2 vagas" */
export function vacancyLabel(count: number): string {
  return `+${count} ${count > 1 ? 'vagas' : 'vaga'}`;
}

/** Dia da semana, dia e mês curtos do selo de data: `{ weekday: 'DOM', day: '11', month: 'OUT' }`. */
export function dateBadge(iso: string): { weekday: string; day: string; month: string } {
  const date = parseDate(iso);

  return {
    weekday: WEEKDAY_SHORT[date.getDay()]!.toUpperCase(),
    day: String(date.getDate()),
    month: MONTH_SHORT[date.getMonth()]!.toUpperCase(),
  };
}

/** "11 de outubro de 2026", "4 a 28 de outubro de 2026", "27 de set a 3 de out de 2026". */
export function sharePeriod(dates: readonly string[]): string {
  const sorted = [...dates].sort();
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  if (!first || !last) return '';

  const a = parseDate(first);
  const b = parseDate(last);

  if (first === last) {
    return `${WEEKDAY_LONG[a.getDay()]}, ${a.getDate()} de ${MONTH_LONG[a.getMonth()]} de ${a.getFullYear()}`;
  }

  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    return `${a.getDate()} a ${b.getDate()} de ${MONTH_LONG[b.getMonth()]} de ${b.getFullYear()}`;
  }

  const year = a.getFullYear() === b.getFullYear() ? '' : ` de ${a.getFullYear()}`;

  return `${a.getDate()} de ${MONTH_SHORT[a.getMonth()]}${year} a ${b.getDate()} de ${MONTH_SHORT[b.getMonth()]} de ${b.getFullYear()}`;
}

export function shareDocument(
  events: readonly ScheduleEvent[],
  lookup: Lookup,
  scope: ShareScope = {}
): ShareDocument {
  const ministryId = scope.ministryId && lookup.ministries.has(scope.ministryId) ? scope.ministryId : null;
  const memberId = scope.memberId && lookup.members.has(scope.memberId) ? scope.memberId : null;

  const shared = [...events]
    .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time))
    .map((event): ShareEvent => {
      const sections = event.ministryIds.flatMap((id): ShareSection[] => {
        const ministry = lookup.ministries.get(id);

        if (!ministry || (ministryId && id !== ministryId)) return [];

        const rows = ministry.roles
          .filter((role) => quantityOf(event, role) > 0)
          .map((role): ShareRow => {
            const ids = event.slots[role.id] ?? [];
            const people = ids.flatMap((personId): SharePerson[] => {
              const member = lookup.members.get(personId);

              return member
                ? [
                    {
                      id: member.id,
                      name: member.name,
                      short: shortName(member.name),
                      photo: member.photo,
                      highlight: member.id === memberId,
                    },
                  ]
                : [];
            });

            return { role: role.name, people, vacancies: Math.max(0, quantityOf(event, role) - ids.length) };
          })
          .filter((row) => !memberId || row.people.some((p) => p.highlight));

        return rows.length
          ? [{ ministryId: id, ministry: ministry.name, color: ministry.color, icon: ministry.icon, rows }]
          : [];
      });

      return {
        id: event.id,
        title: event.title,
        date: event.date,
        time: event.time,
        when: `${formatLongDate(event.date)} · ${event.time}`,
        sections,
      };
    })
    .filter((event) => event.sections.length > 0);

  const ministry = ministryId ? lookup.ministries.get(ministryId) : undefined;

  return withEvents(
    {
      title: '',
      subtitle: '',
      period: '',
      ministry: ministry ? { name: ministry.name, color: ministry.color, icon: ministry.icon } : null,
      member: memberId ? (lookup.members.get(memberId)?.name ?? null) : null,
      events: shared,
      vacancies: 0,
    },
    shared,
    scope.period
  );
}

function vacanciesOf(event: ShareEvent): number {
  return event.sections.reduce((s, x) => s + x.rows.reduce((r, row) => r + row.vacancies, 0), 0);
}

/** O cabeçalho e as contas do documento com estes eventos. */
function withEvents(doc: ShareDocument, events: ShareEvent[], period?: string | null): ShareDocument {
  const vacancies = events.reduce((sum, e) => sum + vacanciesOf(e), 0);
  const only = events.length === 1 ? events[0] : undefined;
  const label = period || sharePeriod(events.map((e) => e.date));
  const open = vacancies
    ? `${vacancies} ${vacancies > 1 ? 'vagas' : 'vaga'} em aberto`
    : 'todas as vagas preenchidas';

  return {
    ...doc,
    title: only ? 'Escala' : `Escala de ${label}`,
    subtitle: only ? sharePeriod([only.date]) : `${events.length} eventos · ${open}`,
    period: label,
    events,
    vacancies,
  };
}

/** Só o evento pedido, com o cabeçalho de um documento de um evento (a imagem "uma por evento"). */
export function singleEventDocument(doc: ShareDocument, event: ShareEvent): ShareDocument {
  return withEvents(doc, [event]);
}

/** Texto com a marcação do WhatsApp (`*negrito*`, `_itálico_`). */
export function shareText(doc: ShareDocument): string {
  const scope = [doc.ministry?.name, doc.member].filter(Boolean).join(' · ');
  const lines = [doc.events.length > 1 ? `*${doc.title}*` : '', scope ? `_${scope}_` : ''].filter(Boolean);
  const header = lines.length ? `${lines.join('\n')}\n\n` : '';
  const body = doc.events
    .map(
      (event) =>
        `*${event.title}*\n${event.when}\n` +
        event.sections
          .map(
            (section) =>
              `\n*${section.ministry}*\n` +
              section.rows
                .map((r) => {
                  const names = r.people.map((p) => p.short).join(', ') || '—';

                  return `${r.role}: ${names}${r.vacancies ? ` (${vacancyLabel(r.vacancies)})` : ''}`;
                })
                .join('\n')
          )
          .join('\n')
    )
    .join('\n\n———\n\n');

  return body ? header + body : '';
}

export function whatsappUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/** Nome do arquivo exportado: "escala-louvor-outubro-de-2026". */
export function shareFileName(doc: ShareDocument): string {
  const base = [
    'escala',
    doc.ministry?.name,
    doc.events.length === 1 ? doc.events[0]?.title : null,
    doc.events.length === 1 ? doc.events[0]?.date : doc.period,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    base
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 80) || 'escala'
  );
}
